import React from 'react';
import type { NodeViewProps } from '@tiptap/react';
import { Box, Button, FormControlLabel, IconButton, Stack, Switch, Tooltip, Typography } from '@mui/material';
import TableChartOutlined from '@mui/icons-material/TableChartOutlined';
import TimelineOutlined from '@mui/icons-material/TimelineOutlined';
import Add from '@mui/icons-material/Add';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { useTranslate } from 'react-admin';
import { BlockFrame } from './BlockFrame';
import { AttrTextField } from './AttrFields';

/* ───────────────────────── Table ───────────────────────── */

export const TableBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const rows = (node.attrs.rows as string[][]) ?? [['', '']];
    const hasHeader = (node.attrs.hasHeader as boolean) ?? true;
    const caption = (node.attrs.caption as string) ?? '';
    const colCount = rows[0]?.length ?? 2;

    const setRows = (next: string[][]) => updateAttributes({ rows: next });
    const updateCell = (r: number, c: number, value: string) => setRows(rows.map((row, ri) => (ri === r ? row.map((cell, ci) => (ci === c ? value : cell)) : [...row])));

    return (
        <BlockFrame
            type="table-block"
            icon={TableChartOutlined}
            title={translate('blockEditor.types.table.label')}
            meta={`${rows.length} × ${colCount}`}
            selected={selected}
            flush
            actions={
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <FormControlLabel sx={{ mr: 0 }} control={<Switch size="small" checked={hasHeader} onChange={(e) => updateAttributes({ hasHeader: e.target.checked })} />} label={<Typography variant="caption">{translate('blockEditor.views.table.header')}</Typography>} />
                    <Tooltip title={translate('blockEditor.views.table.addColumn')}><IconButton size="small" onClick={() => setRows(rows.map((row) => [...row, '']))} aria-label={translate('blockEditor.views.table.addColumn')}><Add fontSize="small" /></IconButton></Tooltip>
                    <Button size="small" startIcon={<Add />} onClick={() => setRows([...rows, Array(colCount).fill('')])}>{translate('blockEditor.views.table.addRow')}</Button>
                </Stack>
            }
        >
            <Box sx={{ overflowX: 'auto', p: 1 }}>
                <table className="oa-block-table">
                    <tbody>
                        {rows.map((row, ri) => (
                            <tr key={ri} className={hasHeader && ri === 0 ? 'is-header' : ''}>
                                {row.map((cell, ci) => (
                                    <td key={ci}>
                                        <AttrTextField size="small" variant="standard" fullWidth value={cell} onValueChange={(v) => updateCell(ri, ci, v)} placeholder={hasHeader && ri === 0 ? translate('blockEditor.views.table.headerPlaceholder', { n: ci + 1 }) : ''} InputProps={{ disableUnderline: true }} inputProps={{ 'aria-label': `${ri + 1}:${ci + 1}` }} />
                                    </td>
                                ))}
                                <td className="oa-block-table-tools">
                                    {rows.length > 1 && (
                                        <IconButton size="small" onClick={() => setRows(rows.filter((_, i) => i !== ri))} aria-label={translate('blockEditor.views.table.removeRow')}><DeleteOutline fontSize="small" /></IconButton>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {colCount > 1 && (
                            <tr className="oa-block-table-colremove">
                                {Array.from({ length: colCount }).map((_, ci) => (
                                    <td key={ci}>
                                        <IconButton size="small" onClick={() => setRows(rows.map((row) => row.filter((_, i) => i !== ci)))} aria-label={translate('blockEditor.views.table.removeColumn')}><DeleteOutline fontSize="small" /></IconButton>
                                    </td>
                                ))}
                                <td />
                            </tr>
                        )}
                    </tbody>
                </table>
            </Box>
            <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid var(--oa-divider)' }}>
                <AttrTextField size="small" variant="standard" fullWidth value={caption} onValueChange={(v) => updateAttributes({ caption: v })} placeholder={translate('blockEditor.views.table.caption')} InputProps={{ disableUnderline: true }} />
            </Box>
        </BlockFrame>
    );
};

/* ───────────────────────── Timeline ───────────────────────── */

interface TimelineEntry { date: string; title: string; description: string }

export const TimelineBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const entries: TimelineEntry[] = Array.isArray(node.attrs.entries) ? (node.attrs.entries as TimelineEntry[]) : [];
    const setEntries = (next: TimelineEntry[]) => updateAttributes({ entries: next });
    const update = (i: number, patch: Partial<TimelineEntry>) => setEntries(entries.map((e, k) => (k === i ? { ...e, ...patch } : e)));

    return (
        <BlockFrame
            type="timeline-block"
            icon={TimelineOutlined}
            title={translate('blockEditor.types.timeline.label')}
            meta={translate('blockEditor.views.timeline.entries', { smart_count: entries.length })}
            selected={selected}
            actions={<Button size="small" startIcon={<Add />} onClick={() => setEntries([...entries, { date: '', title: '', description: '' }])}>{translate('blockEditor.views.timeline.add')}</Button>}
        >
            <Box className="oa-block-timeline">
                {entries.map((entry, i) => (
                    <Box key={i} className="oa-block-timeline-entry">
                        <span className="oa-block-timeline-dot" />
                        <Stack spacing={1} sx={{ flex: 1 }}>
                            <AttrTextField size="small" label={translate('blockEditor.views.timeline.date')} value={entry.date} onValueChange={(v) => update(i, { date: v })} sx={{ maxWidth: 220 }} />
                            <AttrTextField size="small" label={translate('blockEditor.views.timeline.title')} value={entry.title} onValueChange={(v) => update(i, { title: v })} fullWidth />
                            <AttrTextField size="small" multiline minRows={2} label={translate('blockEditor.views.timeline.description')} value={entry.description} onValueChange={(v) => update(i, { description: v })} fullWidth />
                        </Stack>
                        <IconButton size="small" disabled={entries.length <= 1} onClick={() => setEntries(entries.filter((_, k) => k !== i))} aria-label={translate('ra.action.remove')}><DeleteOutline fontSize="small" /></IconButton>
                    </Box>
                ))}
            </Box>
        </BlockFrame>
    );
};
